import clsx from 'clsx'
import React from 'react'
import { MediaDevice } from '@/lib/store'

export type RecordingStatus =
  | {
      status: 'RECORDING'
      elapsedMs: number
    }
  | { status: 'IDLE' }

type Props = {
  inputLevel: number
  outputLevel: number

  availableInputDevices: MediaDevice[]
  selectedInputDeviceId: string
  changeInputDevice: (deviceId: string) => void

  availableOutputDevices: MediaDevice[]
  selectedOutputDeviceId: string | null
  changeOutputDevice: (deviceId: string) => void
}

export default function DeviceSettings({
  inputLevel,
  outputLevel,

  availableInputDevices,
  selectedInputDeviceId,
  changeInputDevice,

  availableOutputDevices,
  selectedOutputDeviceId,
  changeOutputDevice,
}: Props) {
  return (
    <div className="flex flex-col gap-y-4 px-2 py-3 font-sans sm:px-4">
      <div className="flex w-[320px] flex-col sm:w-[420px]">
        <div>
          <div className="flex w-full items-center justify-between">
            <label
              htmlFor="inputDevices"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Input device
            </label>
          </div>
          <select
            id="inputDevices"
            name="inputDevices"
            className="drag-canceller mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6"
            defaultValue={selectedInputDeviceId}
            onChange={(e) => changeInputDevice(e.target.value)}
          >
            {availableInputDevices.map((device) => (
              <option key={`input-${device.deviceId}`} value={device.deviceId}>
                {device.name}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200">
          <div
            className="h-1.5 rounded-full bg-green-400"
            style={{ width: `${inputLevel * 100}%` }}
          ></div>
        </div>
      </div>
      <div className="flex w-[320px] flex-col sm:w-[420px]">
        <div>
          <div className="flex w-full items-center justify-between">
            <label
              htmlFor="outputDevices"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Output device
            </label>
          </div>
          <select
            id="outputDevices"
            name="outputDevices"
            className={clsx(
              'drag-canceller mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6',
              selectedOutputDeviceId === null
                ? 'bg-slate-300'
                : 'focus:ring-2 focus:ring-blue-600',
            )}
            defaultValue={selectedOutputDeviceId ?? 'Default Output'}
            onChange={(e) => changeOutputDevice(e.target.value)}
            disabled={selectedOutputDeviceId === null}
          >
            {availableOutputDevices.length > 0 ? (
              availableOutputDevices.map((device) => (
                <option
                  key={`output-${device.deviceId}`}
                  value={device.deviceId}
                >
                  {device.name}
                </option>
              ))
            ) : (
              <option key={`output-default`}>Default Output</option>
            )}
          </select>
        </div>
        <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200">
          <div
            className="h-1.5 rounded-full bg-green-400"
            style={{ width: `${outputLevel * 100}%` }}
          ></div>
        </div>
      </div>
    </div>
  )
}
